import React, { useEffect, useMemo, useState } from 'react';
import { buildApiUrl } from '../../../config/api';
import { useAuth } from '../../../contexts/AuthContext';
import { useDemo } from '../../../contexts/DemoContext';
import { useSubscription } from '../../../contexts/SubscriptionContext';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import Icon from '../../../components/AppIcon';
import { useUserData } from '../../../hooks/useUserData';
import { formatCurrencyINR } from '../../../utils/currency';
import { getChartTruncationConfig } from '../../../utils/chartUtils';

const AssetAllocationChart = ({ assets, showEvolution = false, selectedYear: selectedYearProp }) => {
  const { userData } = useUserData();
  const { canProjectBeyond5Years } = useSubscription();
  const { isDemoModeActive } = useDemo();
  const startYear = useMemo(() => Number(userData?.setup?.startYear) || new Date().getFullYear(), [userData]);
  const horizon = useMemo(() => Math.max(1, Math.min(60, Number(userData?.setup?.projectionHorizon) || 20)), [userData]);
  
  // Apply 5-year limit for free users
  const truncationConfig = useMemo(() => getChartTruncationConfig('AssetAllocation', canProjectBeyond5Years, isDemoModeActive), [canProjectBeyond5Years, isDemoModeActive]);
  const effectiveHorizon = useMemo(() => truncationConfig.shouldTruncate ? Math.min(horizon, 5) : horizon, [horizon, truncationConfig.shouldTruncate]);
  const years = useMemo(() => Array.from({ length: effectiveHorizon + 1 }, (_, i) => startYear + i), [startYear, effectiveHorizon]);
  const [selectedYear, setSelectedYear] = useState(selectedYearProp || startYear);
  const [chartKey, setChartKey] = useState(`alloc-${selectedYear}`);

  useEffect(() => {
    setSelectedYear(selectedYearProp || startYear);
  }, [selectedYearProp, startYear]);

  useEffect(() => {
    // retrigger pie animation on year change
    setChartKey(`alloc-${selectedYear}`);
  }, [selectedYear]);
  const assetTypes = [
    { name: 'Equity', color: '#1E40AF', value: 0 },
    { name: 'Mutual Funds', color: '#059669', value: 0 },
    { name: 'Real Estate', color: '#F59E0B', value: 0 },
    { name: 'PPF', color: '#8B5CF6', value: 0 },
    { name: 'Fixed Deposits', color: '#EF4444', value: 0 },
    { name: 'Gold', color: '#F97316', value: 0 },
    { name: 'Emergency Fund', color: '#14B8A6', value: 0 }
  ];

  const { getAuthHeaders } = useAuth();
  const { getDemoQueryParams } = useDemo();

  // Target benchmark policy for rebalancing recommendations
  const targetPolicy = {
    'Equity': 60,
    'Real Estate': 20,
    'Mutual Funds': 10,
    'PPF': 5,
    'Fixed Deposits': 3,
    'Gold': 2,
    'Emergency Fund': 5
  };

  const yearsFromNow = Math.max(0, selectedYear - (new Date().getFullYear()));

  // canonicalizeType is imported from shared utils

  const [allocationData, setAllocationData] = useState([]);
  useEffect(() => {
    const fetchAllocation = async () => {
      try {
        const params = new URLSearchParams({ year: String(selectedYear) });
        const demoParams = getDemoQueryParams();
        const separator = params.toString() ? '&' : '';
        const fullParams = `${params.toString()}${demoParams ? separator + demoParams.substring(1) : ''}`;
        const res = await fetch(buildApiUrl(`/api/asset/portfolio/allocation?${fullParams}`), { headers: getAuthHeaders() });
        if (!res.ok) throw new Error('Failed to load allocation');
        const json = await res.json();
        if (json?.success) setAllocationData(json.data);
        else setAllocationData([]);
      } catch (e) {
        console.error(e);
        setAllocationData([]);
      }
    };
    fetchAllocation();
  }, [selectedYear, userData, getDemoQueryParams, getAuthHeaders]);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-floating">
          <p className="font-medium text-foreground">{data.name}</p>
          <p className="text-sm text-muted-foreground">
            {formatCurrencyINR(data.value)} ({data.percentage.toFixed(1)}%)
          </p>
        </div>
      );
    }
    return null;
  };

  const CustomLegend = ({ payload }) => {
    return (
      <div className="flex flex-wrap gap-4 justify-center mt-4">
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center gap-2">
            <div 
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-sm text-muted-foreground">
              {entry.value} ({entry.payload.percentage.toFixed(1)}%)
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="PieChart" size={16} className="text-primary" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">Asset Allocation</h3>
            <p className="text-sm text-muted-foreground">Portfolio distribution by asset class</p>
          </div>
        </div>
        
        {showEvolution && (
          <div className="flex items-center gap-2">
            <Icon name="Calendar" size={16} className="text-muted-foreground" />
            <select 
              className="text-sm border border-border rounded px-2 py-1 bg-background"
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
            >
              {years.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {allocationData.length > 0 ? (
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart key={chartKey}>
              <Pie
                data={allocationData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={120}
                paddingAngle={2}
                dataKey="value"
                isAnimationActive
                animationBegin={0}
                animationDuration={600}
              >
                {allocationData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend content={<CustomLegend />} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="h-80 flex items-center justify-center">
          <div className="text-center">
            <Icon name="PieChart" size={48} className="text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No assets to display</p>
            <p className="text-sm text-muted-foreground">Add assets to see allocation breakdown</p>
          </div>
        </div>
      )}

      {/* Rebalancing Recommendations */}
      {allocationData.length > 0 && (
        <div className="mt-6 pt-6 border-t border-border">
          <h4 className="font-medium text-foreground mb-1">Rebalancing Recommendations</h4>
          <p className="text-xs text-muted-foreground mb-3">
            Benchmarks: Equity 60%, Real Estate 20%, others as noted. Flagged when deviation exceeds ±5%.
          </p>
          <BackendRebalancing allocationData={allocationData} />
        </div>
      )}
    </div>
  );
};

const BackendRebalancing = ({ allocationData }) => {
  const { getAuthHeaders } = useAuth();
  const { getDemoQueryParams } = useDemo();
  const [items, setItems] = useState([]);
  useEffect(() => {
    const fetchRebalancing = async () => {
      try {
        const queryParams = getDemoQueryParams();
        const res = await fetch(buildApiUrl(`/api/asset/portfolio/rebalancing${queryParams}`), { headers: getAuthHeaders() });
        if (!res.ok) throw new Error('Failed to load rebalancing');
        const json = await res.json();
        if (json?.success) setItems(json.data || []);
        else setItems([]);
      } catch (e) {
        console.error(e);
        setItems([]);
      }
    };
    fetchRebalancing();
  }, [allocationData, getDemoQueryParams, getAuthHeaders]);
  if (!items.length) return null;
  return (
    <div className="space-y-2">
      {items.map((asset, index) => {
        const isOverweight = asset.direction === 'over';
        return (
          <div key={index} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-3">
              <div 
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: asset.color }}
              />
              <span className="text-sm font-medium">{asset.name}</span>
              <span className="text-xs text-muted-foreground">({asset.percentage.toFixed(1)}% vs {asset.target}%)</span>
            </div>
            <div className="flex items-center gap-3 min-w-[220px]">
              <div className="flex-1 h-2 bg-background rounded-full overflow-hidden">
                <div
                  className={`h-2 ${isOverweight ? 'bg-warning' : 'bg-accent'}`}
                  style={{ width: `${Math.min(100, Math.max(0, asset.percentage))}%` }}
                />
              </div>
              <span className={`text-sm ${isOverweight ? 'text-warning' : 'text-accent'}`}>
                {isOverweight ? 'Over' : 'Under'} by {Math.abs(asset.delta).toFixed(1)}%
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default AssetAllocationChart;